declare const Chart: any;
import AccountManager from './accountManager.js';
import d from './DOM.js';
import Toast from './toast.js';
import { withCsrf } from './csrf.js';
// Same pure module the server answers GET /api/account-growth with, so the two never disagree.
import { projectGoal, ProjectionResult } from '../../../apps/web/src/tv/pages/account-growth-projection.js';

/**
 * /account-growth — account size broken into deposits, broker credit and trading P&L, a goal,
 * and a prediction of when the goal is reached at the current average monthly return.
 * Server: apps/web/src/tv/pages/account-growth.ts. The projection maths is the server's own module, bundled,
 * so the live preview while typing a goal matches what the server returns after saving.
 */

const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);
const moneyShort = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const pct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const monthLabel = (key: string, long = false) => {
	const [y, m] = key.split('-').map(Number);
	return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: long ? 'long' : 'short', year: 'numeric' });
};

export default abstract class AccountGrowth {
	private static data: any = null;
	private static chart: any = null;

	public static init() {
		if (!AccountManager.currentAccount) {
			d.qs<HTMLElement>('#growth-empty').classList.remove('hidden');
			d.qs<HTMLElement>('#growth-content').classList.add('hidden');
			return;
		}

		const dateInput = d.qs<HTMLInputElement>('#growth-flow-date');
		const t = new Date();
		dateInput.value = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;

		d.qs<HTMLFormElement>('#growth-goal-form').addEventListener('submit', e => { e.preventDefault(); AccountGrowth.saveGoal(); });
		d.qs<HTMLButtonElement>('#growth-goal-clear').addEventListener('click', () => AccountGrowth.clearGoal());
		d.qs<HTMLFormElement>('#growth-flow-form').addEventListener('submit', e => { e.preventDefault(); AccountGrowth.addFlow(); });
		// Live preview of the prediction while editing, before saving.
		['#growth-goal-amount', '#growth-goal-contribution'].forEach(sel =>
			d.qs<HTMLInputElement>(sel).addEventListener('input', () => AccountGrowth.renderPrediction(true)));

		AccountGrowth.load();
	}

	private static get accountNumber() { return AccountManager.currentAccount!.accountNumber; }

	private static async load() {
		try {
			const res = await fetch(`/api/account-growth?accountNumber=${encodeURIComponent(AccountGrowth.accountNumber)}`);
			if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
			AccountGrowth.data = await res.json();
		} catch (err) {
			console.error('Failed to load account growth', err);
			Toast.show('Could not load account growth', 'error');
			return;
		}

		const { goal } = AccountGrowth.data;
		d.qs<HTMLInputElement>('#growth-goal-amount').value = goal ? String(goal.goalAmount) : '';
		d.qs<HTMLInputElement>('#growth-goal-contribution').value = goal && goal.monthlyContribution ? String(goal.monthlyContribution) : '';
		d.qs<HTMLElement>('#growth-goal-clear').classList.toggle('hidden', !goal);

		AccountGrowth.renderSummary();
		AccountGrowth.renderPrediction(false);
		AccountGrowth.renderHistory();
		AccountGrowth.renderFlows();
	}

	private static renderSummary() {
		const { currentBalance, breakdown } = AccountGrowth.data;
		d.qs<HTMLElement>('#growth-balance').textContent = money(currentBalance);
		d.qs<HTMLElement>('#growth-deposits').textContent = money(breakdown.deposits);
		d.qs<HTMLElement>('#growth-deposits-sub').textContent = breakdown.withdrawals > 0 ? `${money(breakdown.withdrawals)} withdrawn` : 'No withdrawals';
		d.qs<HTMLElement>('#growth-credit').textContent = money(breakdown.credit);
		d.qs<HTMLElement>('#growth-credit-sub').textContent = breakdown.manual.credit > 0 ? `incl. ${money(breakdown.manual.credit)} recorded` : 'From broker history';
		const pnl = d.qs<HTMLElement>('#growth-pnl');
		pnl.textContent = money(breakdown.tradingPnl);
		pnl.style.color = breakdown.tradingPnl >= 0 ? 'var(--theme-status-profit)' : 'var(--theme-status-loss)';
		const avg = AccountGrowth.data.avgMonthlyReturnPercent;
		d.qs<HTMLElement>('#growth-pnl-sub').textContent = avg === null ? 'All time' : `Avg ${pct(avg)} / month`;
		d.qs<HTMLElement>('#growth-balance-sub').textContent = breakdown.manual.deposits > 0 ? `incl. ${money(breakdown.manual.deposits)} recorded deposits` : 'Same balance as Broker Accounts';

		const note = d.qs<HTMLElement>('#growth-unattributed');
		if (Math.abs(breakdown.unattributed) >= 1) {
			note.classList.remove('hidden');
			note.innerHTML = `<strong>${money(breakdown.unattributed)}</strong> of your account size isn't explained by the deposits, credit and trades on record — usually deposits made before your trade history was synced. Record them below to complete the breakdown.`;
		} else {
			note.classList.add('hidden');
		}
	}

	/** Goal inputs as numbers (NaN when blank/invalid). */
	private static goalInputs() {
		const goal = parseFloat(d.qs<HTMLInputElement>('#growth-goal-amount').value);
		const contribution = parseFloat(d.qs<HTMLInputElement>('#growth-goal-contribution').value) || 0;
		return { goal, contribution };
	}

	private static renderPrediction(preview: boolean) {
		const data = AccountGrowth.data;
		if (!data) { return; }
		const { goal, contribution } = AccountGrowth.goalInputs();
		const text = d.qs<HTMLElement>('#growth-prediction-text');
		const basis = d.qs<HTMLElement>('#growth-prediction-basis');
		const progress = d.qs<HTMLElement>('#growth-goal-progress');
		const avg: number | null = data.avgMonthlyReturnPercent;

		if (!(goal > 0)) {
			text.textContent = 'Set a goal to see when you\'ll reach it.';
			basis.textContent = '';
			progress.classList.add('hidden');
			AccountGrowth.renderChart(null, null);
			return;
		}

		// Saved goal → server projection; while editing → same function, locally.
		const projection: ProjectionResult = !preview && data.projection
			? data.projection
			: projectGoal({ currentBalance: data.currentBalance, goalAmount: goal, monthlyReturnPercent: avg ?? 0, monthlyContribution: contribution });

		const pctDone = Math.max(0, Math.min(100, (data.currentBalance / goal) * 100));
		progress.classList.remove('hidden');
		d.qs<HTMLElement>('#growth-goal-progress-label').textContent = `${money(data.currentBalance)} of ${moneyShort(goal)}`;
		d.qs<HTMLElement>('#growth-goal-progress-pct').textContent = `${pctDone.toFixed(1)}%`;
		d.qs<HTMLElement>('#growth-goal-progress-fill').style.width = `${pctDone}%`;

		const goalStr = moneyShort(goal);
		if (projection.status === 'reached') {
			text.innerHTML = `You've already reached <strong>${goalStr}</strong> — time to set a bigger goal.`;
		} else if (projection.status === 'projected') {
			const years = Math.floor(projection.months! / 12);
			const months = projection.months! % 12;
			const span = [years ? `${years} year${years === 1 ? '' : 's'}` : '', months ? `${months} month${months === 1 ? '' : 's'}` : ''].filter(Boolean).join(' ');
			text.innerHTML = `Based on your current account size and return, you will reach <strong>${goalStr}</strong> in <strong>${monthLabel(projection.targetMonth!, true)}</strong> <span class="growth-prediction-span">(${span} from now)</span>.`;
		} else if (avg === null) {
			text.innerHTML = `There isn't enough trading history yet to predict when you'll reach <strong>${goalStr}</strong>.${contribution > 0 ? '' : ' Add a monthly contribution to project from deposits alone.'}`;
		} else {
			text.innerHTML = `At your current rate of return you won't reach <strong>${goalStr}</strong> — your average month is ${pct(avg)}.${contribution > 0 ? '' : ' A monthly contribution would change that.'}`;
		}

		const parts: string[] = [];
		if (avg !== null) { parts.push(`Average monthly trading return of ${pct(avg)} over your last ${data.returnLookbackMonths} active month${data.returnLookbackMonths === 1 ? '' : 's'}, compounded monthly`); }
		if (contribution > 0) { parts.push(`plus ${money(contribution)} added each month`); }
		basis.textContent = parts.length ? `${parts.join(', ')}. Past performance doesn't guarantee future results.` : '';
		if (preview) { basis.textContent += ' (Preview — save to keep this goal.)'; }

		AccountGrowth.renderChart(projection, goal);
	}

	private static renderChart(projection: ProjectionResult | null, goal: number | null) {
		const canvas = d.qs<HTMLCanvasElement>('#growthChart');
		if (!canvas || typeof Chart === 'undefined') { return; }
		const history: any[] = AccountGrowth.data.history;

		const actualLabels = history.map(h => h.month);
		const projLabels = (projection?.path || []).map(p => p.month).filter(m => !actualLabels.includes(m));
		const labels = [...actualLabels, ...projLabels];
		const actual = [...history.map(h => h.endBalance), ...projLabels.map(() => null)];
		// Projection starts from the latest actual point so the dashed line joins the solid one.
		const projected = labels.map(() => null as number | null);
		if (projection && projection.path.length) {
			const lastIdx = actualLabels.length - 1;
			if (lastIdx >= 0) { projected[lastIdx] = AccountGrowth.data.currentBalance; }
			for (const p of projection.path) {
				const i = labels.indexOf(p.month);
				if (i >= 0) { projected[i] = p.balance; }
			}
		}

		const datasets: any[] = [
			{ label: 'Account size', data: actual, borderColor: '#00d2ff', backgroundColor: 'rgba(0, 210, 255, 0.12)', fill: true, tension: 0.25, pointRadius: 2, spanGaps: false },
		];
		if (projection && projection.path.length) {
			datasets.push({ label: 'Projected', data: projected, borderColor: '#7d5fff', borderDash: [6, 5], pointRadius: 0, fill: false, tension: 0.25, spanGaps: true });
		}
		if (goal) {
			datasets.push({ label: 'Goal', data: labels.map(() => goal), borderColor: '#10B981', borderWidth: 1.5, borderDash: [2, 4], pointRadius: 0, fill: false });
		}

		if (AccountGrowth.chart) { AccountGrowth.chart.destroy(); }
		AccountGrowth.chart = new Chart(canvas.getContext('2d'), {
			type: 'line',
			data: { labels: labels.map(m => monthLabel(m)), datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				interaction: { mode: 'index', intersect: false },
				plugins: {
					legend: { display: true, labels: { color: '#94A3B8', boxWidth: 12 } },
					tooltip: { callbacks: { label: (ctx: any) => ctx.parsed.y === null ? '' : `${ctx.dataset.label}: ${money(ctx.parsed.y)}` } },
				},
				scales: {
					x: { grid: { display: false }, ticks: { maxTicksLimit: 10, color: '#94A3B8' } },
					y: { grid: { color: '#1E293B' }, ticks: { color: '#94A3B8', callback: (v: number) => moneyShort(v) } },
				},
			},
		});
	}

	private static renderHistory() {
		const history: any[] = [...AccountGrowth.data.history].reverse();
		const body = d.qs<HTMLElement>('#growth-history');
		if (history.length === 0) {
			body.innerHTML = '<tr><td colspan="7" style="text-align: center; color: var(--theme-text-secondary);">No trading history yet.</td></tr>';
			return;
		}
		const cls = (n: number) => (n > 0 ? 'text-profit' : n < 0 ? 'text-loss' : '');
		body.innerHTML = history.map(h => `
			<tr>
				<td>${monthLabel(h.month)}</td>
				<td>${money(h.startBalance)}</td>
				<td>${h.deposits || h.withdrawals ? `${h.deposits ? money(h.deposits) : ''}${h.withdrawals ? ` <span class="text-loss">−${money(h.withdrawals)}</span>` : ''}` : '—'}</td>
				<td>${h.credit ? money(h.credit) : '—'}</td>
				<td class="${cls(h.tradingPnl)}">${h.tradingPnl ? money(h.tradingPnl) : '—'}</td>
				<td class="${cls(h.returnPercent ?? 0)}">${h.returnPercent === null ? '—' : pct(h.returnPercent)}</td>
				<td>${money(h.endBalance)}</td>
			</tr>
		`).join('');
	}

	private static renderFlows() {
		const flows: any[] = AccountGrowth.data.cashflows;
		const container = d.qs<HTMLElement>('#growth-flows');
		if (flows.length === 0) {
			container.innerHTML = '<div class="growth-flows-empty">Nothing recorded yet.</div>';
			return;
		}
		const label: Record<string, string> = { deposit: 'Deposit', withdrawal: 'Withdrawal', credit: 'Broker credit' };
		container.innerHTML = flows.map(f => `
			<div class="growth-flow">
				<div>
					<strong>${label[f.flowType] || esc(String(f.flowType))}</strong>
					<span class="growth-flow-date">${esc(String(f.flowDate))}</span>
					${f.note ? `<div class="growth-flow-note">${esc(String(f.note))}</div>` : ''}
				</div>
				<div style="display: flex; align-items: center; gap: 10px;">
					<span class="${f.flowType === 'withdrawal' ? 'text-loss' : ''}">${f.flowType === 'withdrawal' ? '−' : ''}${money(f.amount)}</span>
					<button type="button" class="growth-flow-delete" data-id="${Number(f.id)}" title="Remove" aria-label="Remove">&times;</button>
				</div>
			</div>
		`).join('');
		container.querySelectorAll<HTMLButtonElement>('.growth-flow-delete').forEach(btn =>
			btn.addEventListener('click', () => AccountGrowth.deleteFlow(Number(btn.dataset.id))));
	}

	private static async post(url: string, body: any, method = 'POST') {
		const res = await fetch(url, { method, headers: withCsrf({ 'Content-Type': 'application/json' }), body: body ? JSON.stringify(body) : undefined });
		const json = await res.json().catch(() => ({}));
		if (!res.ok) { throw new Error(json.error || `HTTP ${res.status}`); }
		return json;
	}

	private static async saveGoal() {
		const { goal, contribution } = AccountGrowth.goalInputs();
		if (!(goal > 0)) { Toast.show('Enter a goal amount above zero', 'error'); return; }
		try {
			await AccountGrowth.post('/api/account-growth/goal', { accountNumber: AccountGrowth.accountNumber, goalAmount: goal, monthlyContribution: contribution });
			Toast.show('Goal saved', 'success', 2500);
			await AccountGrowth.load();
		} catch (err) {
			Toast.show((err as Error).message || 'Could not save goal', 'error');
		}
	}

	private static async clearGoal() {
		try {
			await AccountGrowth.post(`/api/account-growth/goal/${encodeURIComponent(AccountGrowth.accountNumber)}`, null, 'DELETE');
			await AccountGrowth.load();
		} catch (err) {
			Toast.show((err as Error).message || 'Could not clear goal', 'error');
		}
	}

	private static async addFlow() {
		const body = {
			accountNumber: AccountGrowth.accountNumber,
			flowType: d.qs<HTMLSelectElement>('#growth-flow-type').value,
			amount: parseFloat(d.qs<HTMLInputElement>('#growth-flow-amount').value),
			flowDate: d.qs<HTMLInputElement>('#growth-flow-date').value,
			note: d.qs<HTMLInputElement>('#growth-flow-note').value,
		};
		try {
			await AccountGrowth.post('/api/account-growth/cashflow', body);
			d.qs<HTMLInputElement>('#growth-flow-amount').value = '';
			d.qs<HTMLInputElement>('#growth-flow-note').value = '';
			Toast.show('Recorded', 'success', 2000);
			await AccountGrowth.load();
		} catch (err) {
			Toast.show((err as Error).message || 'Could not record that', 'error');
		}
	}

	private static async deleteFlow(id: number) {
		if (!confirm('Remove this entry?')) { return; }
		try {
			await AccountGrowth.post(`/api/account-growth/cashflow/${id}`, null, 'DELETE');
			await AccountGrowth.load();
		} catch (err) {
			Toast.show((err as Error).message || 'Could not remove that', 'error');
		}
	}
}
